import React, { useState, useEffect } from 'react';
import { ArrowLeft, Coins, CheckCircle, Loader2, Star, CreditCard, Zap, Shield, Gift, TrendingUp } from 'lucide-react';
import { 
  getCreditPackages, 
  getPaymentLink,
  redirectToPayment, 
  formatPrice,
  getTransactionHistory,
  Transaction
} from '../services/paymentService';
import { CreditPackage, User } from '../types';

interface BuyCreditsPageProps {
  user: User;
  onBack: () => void;
}

const BuyCreditsPage: React.FC<BuyCreditsPageProps> = ({ user, onBack }) => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pkgs, txns] = await Promise.all([
        getCreditPackages(),
        getTransactionHistory(10),
      ]);
      setPackages(pkgs);
      setTransactions(txns);
    } catch (err) {
      setError('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId: string) => {
    setPurchasing(packageId);
    setError('');

    try {
      // Get the Payment Link URL with user identification
      const paymentUrl = await getPaymentLink(packageId);
      
      if (paymentUrl) {
        // Redirect to Stripe Payment Page
        redirectToPayment(paymentUrl);
      } else {
        setError('Erro ao obter link de pagamento.');
      }
    } catch (err) {
      setError('Erro ao processar pagamento. Tente novamente.');
      setPurchasing(null);
    }
    // Note: Don't reset purchasing state on success since we're redirecting
  };

  const formatDate = (timestamp: { _seconds: number }) => {
    return new Date(timestamp._seconds * 1000).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>
      </div>

      {/* Balance Card */}
      <div className="bg-gradient-to-br from-emerald-900/50 to-slate-900 rounded-2xl border border-emerald-500/30 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-xl">
              <Coins className="w-8 h-8 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Seu Saldo Atual</p>
              <h2 className="text-4xl font-bold text-white">{user.credits} <span className="text-xl font-normal text-slate-400">créditos</span></h2>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <Gift className="w-4 h-4" />
            <span>Créditos nunca expiram!</span>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-400/10 border border-red-400/20 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Packages */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="w-6 h-6 text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">Escolha seu Pacote</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => {
            const pricePerCredit = pkg.price / pkg.credits;
            const bestValue = pkg.id === 'pkg_50';
            
            return (
              <div 
                key={pkg.id}
                className={`relative p-6 rounded-2xl border transition-all hover:scale-[1.02] ${
                  pkg.popular 
                    ? 'bg-gradient-to-br from-emerald-900/50 to-slate-900 border-emerald-500/50 shadow-lg shadow-emerald-500/10' 
                    : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full shadow-lg">
                      <Star className="w-3 h-3" /> Mais Popular
                    </span>
                  </div>
                )}
                
                {bestValue && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-500 text-slate-900 text-xs font-bold rounded-full shadow-lg">
                      <Zap className="w-3 h-3" /> Melhor Valor
                    </span>
                  </div>
                )}

                <div className="text-center mb-6 pt-2">
                  <div className="inline-flex items-center gap-2 text-4xl font-bold text-white mb-1">
                    <Zap className="w-8 h-8 text-yellow-400" />
                    {pkg.credits}
                  </div>
                  <p className="text-slate-400 text-sm">créditos</p>
                </div>

                <div className="text-center mb-6">
                  <span className="text-3xl font-bold text-white">{formatPrice(pkg.price)}</span>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatPrice(pricePerCredit)} por crédito
                  </p>
                </div>

                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing !== null}
                  className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                      : 'bg-slate-800 hover:bg-slate-700 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {purchasing === pkg.id ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Comprar
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            <CreditCard className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Cartão de Crédito</p>
            <p className="text-xs text-slate-400">Visa, Master, Elo, Amex</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
          <div className="p-2 bg-emerald-500/10 rounded-lg">
            <Zap className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">PIX</p>
            <p className="text-xs text-slate-400">Créditos instantâneos</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Shield className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Boleto Bancário</p>
            <p className="text-xs text-slate-400">Até 3 dias úteis</p>
          </div>
        </div>
      </div>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-3 p-4 bg-slate-900/30 rounded-xl">
        <Shield className="w-5 h-5 text-emerald-400" />
        <p className="text-sm text-slate-400">
          Pagamento seguro processado por <span className="text-white font-medium">Stripe</span>. Seus dados estão protegidos.
        </p>
      </div>

      {/* Transaction History */}
      {transactions.length > 0 && (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h3 className="text-lg font-bold text-white">Histórico de Transações</h3>
          </div>
          <div className="divide-y divide-slate-800">
            {transactions.slice(0, 5).map((tx) => (
              <div key={tx.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    tx.type === 'purchase' ? 'bg-emerald-500/10' :
                    tx.type === 'bonus' ? 'bg-yellow-500/10' :
                    tx.type === 'consumption' ? 'bg-blue-500/10' :
                    'bg-slate-700'
                  }`}>
                    {tx.type === 'purchase' && <CreditCard className="w-4 h-4 text-emerald-400" />}
                    {tx.type === 'bonus' && <Gift className="w-4 h-4 text-yellow-400" />}
                    {tx.type === 'consumption' && <Zap className="w-4 h-4 text-blue-400" />}
                    {tx.type === 'refund' && <ArrowLeft className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{tx.description}</p>
                    <p className="text-xs text-slate-500">{formatDate(tx.createdAt)}</p>
                  </div>
                </div>
                <div className={`text-sm font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount} créditos
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyCreditsPage;

